import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

interface ScaledCanvasProps {
  /** Medidas da tela de desenho, em px. */
  width: number;
  height: number;
  /** Abaixo disso o texto fica ilegível; a tela passa a ser cortada à direita. */
  minScale?: number;
  /** Descrição para leitor de tela — o conteúdo desenhado é decorativo. */
  label: string;
  className?: string;
  /**
   * O que fica por cima da tela e pode sair da moldura (a Lexi espiando).
   * Recebe a escala em `--canvas-scale`, para medir em pixels da tela de desenho.
   */
  overlay?: ReactNode;
  /** Espaço reservado acima da moldura para o overlay, em pixels da tela de desenho. */
  overlaySpace?: number;
  children: ReactNode;
}

/**
 * Desenha a interface numa tela de tamanho fixo e escala tudo junto para
 * caber na largura disponível. A composição não se rearruma em cada
 * breakpoint: o que se vê no celular é a mesma tela, menor.
 */
export function ScaledCanvas({
  width,
  height,
  minScale = 0.5,
  label,
  className,
  overlay,
  overlaySpace = 0,
  children,
}: ScaledCanvasProps) {
  const frame = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const element = frame.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setScale(Math.max(Math.min(entry.contentRect.width / width, 1), minScale));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [width, minScale]);

  return (
    <div
      className="relative"
      style={{ '--canvas-scale': scale, paddingTop: overlaySpace * scale } as CSSProperties}
    >
      <div
        ref={frame}
        className={`relative w-full overflow-hidden ${className ?? ''}`}
        style={{ height: height * scale }}
      >
        <div
          role="img"
          aria-label={label}
          className="origin-top-left"
          style={{ width, height, transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
      {overlay}
    </div>
  );
}
